import React from "react";
import { DropdownChevronPair } from "./DropdownChevronPair";

/** Sidebar-friendly labeled select: opens upward, full width (matches Dept / Team selectors) */
export function SidebarSelect({
  label,
  valueLabel,
  placeholder,
  isOpen,
  onToggle,
  children,
  disabled,
  busy,
}: {
  label: string;
  valueLabel: string;
  placeholder: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <div className="relative mb-3 last:mb-0">
      <span className="block text-[11px] font-primary font-semibold uppercase tracking-[0.06em] text-white/55 mb-1.5">
        {label}
      </span>
      <button
        type="button"
        disabled={disabled || busy}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => {
          if (disabled || busy) return;
          onToggle();
        }}
        className={`
          group w-full flex items-center justify-between gap-2 min-h-[44px]
          rounded-xl px-3 py-2.5 text-left font-primary
          border transition-all duration-200
          ${
            disabled || busy
              ? "bg-white/5 border-white/10 text-white/40 cursor-not-allowed"
              : "bg-white/10 border-white/20 text-white hover:bg-white/[0.14] hover:border-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#26295e]"
          }
        `}
      >
        <span className="truncate text-[15px] font-medium pr-1">
          {busy ? (
            <span className="text-white/50 animate-pulse">Loading…</span>
          ) : valueLabel ? (
            valueLabel
          ) : (
            <span className="text-white/50">{placeholder}</span>
          )}
        </span>
        <DropdownChevronPair className="border-white/30 bg-white/[0.08] text-white/85" />
      </button>

      {isOpen && !busy && (
        <div
          className="absolute bottom-full left-0 right-0 z-[60] mb-2 rounded-xl bg-white shadow-[0_-8px_32px_rgba(0,0,0,0.18)] border border-[#e8ecf4] overflow-hidden max-h-[min(280px,50vh)] flex flex-col"
          role="listbox"
        >
          <div className="overflow-y-auto overscroll-contain py-1">
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

export function SidebarSelectOption({
  selected,
  onSelect,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      className={`w-full text-left px-3 py-2.5 text-[14px] font-primary transition-colors border-0 cursor-pointer
        ${
          selected
            ? "bg-[#eef1fb] text-[#26295e] font-semibold"
            : "text-[#26295e] hover:bg-[#f4f6fa] active:bg-[#e8ecf4]"
        }
      `}
    >
      <span className="block truncate">{children}</span>
    </button>
  );
}
